/**
 * Referral and campaign links — docs/26-referrals-and-attribution.md, 2.3–2.6.
 *
 * The string that leaves drbell.app inside a Play Store URL comes back, days
 * later, out of `getInstallReferrerAsync()` on a phone. The landing page
 * (apps/web/worker) builds it and the app (apps/mobile) reads it, so both
 * halves live here: a builder and a parser that disagree about one character
 * are an attribution that silently never happens, and nothing anywhere would
 * say so — the person just installs DrBell and the pharmacy that sent them
 * is never credited.
 *
 * Pure string work, no `URL` or `URLSearchParams`. Hermes ships only part of
 * `URLSearchParams` (no `get` on some React Native versions), and the rule
 * for this package is that the same input gives the same answer in Hermes
 * and in workerd.
 */

/** The Android application id, which is also the Play listing's `id=`. */
export const ANDROID_PACKAGE = 'app.drbell.mobile';

/**
 * The App Store's numeric "Apple ID" for DrBell (App Store Connect → App
 * Information). An App Store URL takes this, never the bundle id.
 */
export const APP_STORE_ID = '6816480911';

/**
 * Whether DrBell is on the App Store yet. Until Apple approves the build the
 * listing is a 404, so nothing may link to it: the website says "coming"
 * (apps/web facts.ts) and the phone's "Rate DrBell" has no listing to fall
 * back to on an iPhone. True since 1 November 2026, the day version 1.0
 * went live on the App Store.
 */
export const APP_STORE_LIVE = true;

/**
 * App Store Connect's campaign "provider token" (`pt`), or null until it is
 * copied in.
 *
 * TODO(owner): App Store Connect → Apps → DrBell → Analytics → Acquisition →
 * Campaigns → "Generate a campaign link". The link it makes contains
 * `pt=<digits>`; put the digits here. It is not a secret — it is in every
 * campaign link Apple generates — but it is per developer account and is in
 * no file in this repository. While it is null the App Store button carries
 * no campaign at all, because `ct` without `pt` is ignored by Apple.
 */
export const APP_STORE_PROVIDER_TOKEN: string | null = null;

/** The five UTM fields, by their query-string names. */
export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

/**
 * Ad platforms' own click ids. Recorded as they came — `gclid` is
 * case-sensitive, so these are never lower-cased.
 */
export const CLICK_ID_KEYS = [
  'gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'msclkid', 'li_fat_id', 'twclid', 'sccid', 'epik',
] as const;

export interface Utm {
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
}

/** Each value is capped: a query string is where people paste whole paragraphs. */
const MAX_VALUE = 100;

/**
 * A query string as a plain object. First value wins; `+` is a space (it is
 * how forms and most ad platforms encode one); a value that does not decode
 * is dropped rather than throwing, because a hand-edited link with a stray
 * `%` must still land on the page.
 */
export function parseQuery(query: string): Record<string, string> {
  const out: Record<string, string> = {};
  const q = query.startsWith('?') ? query.slice(1) : query;
  if (!q) return out;
  for (const pair of q.split('&')) {
    if (!pair) continue;
    const eq = pair.indexOf('=');
    const rawKey = eq === -1 ? pair : pair.slice(0, eq);
    const rawValue = eq === -1 ? '' : pair.slice(eq + 1);
    let key: string;
    let value: string;
    try {
      key = decodeURIComponent(rawKey.replace(/\+/g, ' '));
      value = decodeURIComponent(rawValue.replace(/\+/g, ' '));
    } catch {
      continue;
    }
    if (key && !(key in out)) out[key] = value;
  }
  return out;
}

const clean = (v: string | undefined) => {
  const t = (v ?? '').trim();
  return t ? t.slice(0, MAX_VALUE) : undefined;
};

/**
 * UTM and click ids out of a query, and nothing else.
 *
 * An allow-list, never "every parameter": a query string is where people
 * paste phone numbers, and whatever is read here is written to a log.
 * UTM values are lower-cased so `Instagram` and `instagram` are one row in
 * a report; click ids keep their case, because the ad platform's own lookup
 * is case-sensitive.
 */
export function attributionFromQuery(params: Record<string, string>): {
  utm: Utm;
  ids: Record<string, string>;
} {
  const utm: Utm = {};
  for (const key of UTM_KEYS) {
    const v = clean(params[key]);
    if (v) utm[key.slice(4) as keyof Utm] = v.toLowerCase();
  }
  const ids: Record<string, string> = {};
  for (const key of CLICK_ID_KEYS) {
    const v = clean(params[key]);
    if (v) ids[key] = v;
  }
  return { utm, ids };
}

/**
 * A referral code as typed, pasted or found in a link — or null.
 *
 * Codes are stored upper-case (docs/26, `referral_codes.code`): six
 * generated characters, or a 4–16 character vanity code. People type them
 * with spaces ("K7R M4Q"), dashes, in lower case, and paste them with the
 * "DRBELL " label the landing page shows above them, so all of that is
 * forgiven here; anything left that is not letters and digits is not a code.
 */
export function normaliseReferralCode(input: string | null | undefined): string | null {
  if (!input) return null;
  let s = input.trim().toUpperCase().replace(/[\s\-_.·]+/g, '');
  if (s.startsWith('DRBELL') && s.length > 'DRBELL'.length + 3) s = s.slice('DRBELL'.length);
  if (s.startsWith('CODE:')) s = s.slice(5);
  return /^[A-Z0-9]{4,16}$/.test(s) ? s : null;
}

/**
 * A click id from the server: `clk_` and random characters in practice, but
 * only its shape is checked here — it is an opaque key the server issued, and
 * it travels through Play's referrer and back.
 */
const CLICK_ID = /^[A-Za-z0-9_-]{6,64}$/;

export function isClickId(v: string | null | undefined): v is string {
  return typeof v === 'string' && CLICK_ID.test(v);
}

/**
 * Play keeps the referrer for 90 days and hands it to the app verbatim, but
 * Google advises keeping it short; past roughly 1 KB some store paths
 * truncate it. The click id goes first so a truncation can never lose it.
 */
const MAX_REFERRER = 400;

/** The parameter names inside Play's referrer. Prefixed, so a UTM tool never collides. */
export const REFERRER_CLICK = 'drbell_click';
export const REFERRER_CODE = 'drbell_code';

/**
 * The `referrer=` value for the Play Store link — before it is URL-encoded
 * into that link.
 *
 * `drbell_click` is the claim: the server's click row holds everything else
 * (every UTM, every ad click id, the landing path). `drbell_code` is the
 * fallback when the click could not be recorded. The UTM fields ride along
 * because Play Console's own acquisition report reads `utm_source` and
 * `utm_campaign` from exactly this string.
 */
export function playReferrer(input: { clickId?: string | null; code?: string | null; utm?: Utm }): string {
  const pairs: Array<[string, string]> = [];
  if (isClickId(input.clickId)) pairs.push([REFERRER_CLICK, input.clickId]);
  const code = normaliseReferralCode(input.code);
  if (code) pairs.push([REFERRER_CODE, code]);
  // Most useful first: whatever does not fit is dropped from the end.
  const order: Array<keyof Utm> = ['source', 'medium', 'campaign', 'content', 'term'];
  for (const key of order) {
    const v = input.utm?.[key];
    if (v) pairs.push([`utm_${key}`, v.slice(0, MAX_VALUE)]);
  }
  let out = '';
  for (const [k, v] of pairs) {
    const next = `${out ? '&' : ''}${k}=${encodeURIComponent(v)}`;
    if (encodeURIComponent(out + next).length > MAX_REFERRER) break;
    out += next;
  }
  return out;
}

/** The Play listing, carrying a referrer when there is one. */
export function playStoreUrl(referrer?: string | null): string {
  const base = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;
  return referrer ? `${base}&referrer=${encodeURIComponent(referrer)}` : base;
}

/**
 * The App Store listing.
 *
 * `pt` + `ct` make App Analytics count downloads per campaign — in
 * aggregate, last click, within 24 hours, and nothing under five. That is a
 * report, never an attribution: Apple never tells the app which link it
 * came from, which is why the iPhone half of a referral is the code the
 * person copies or types (docs/26, 2.5). `ct` is at most 30 characters.
 */
export function appStoreUrl(input: { campaign?: string | null; providerToken?: string | null } = {}): string {
  const base = `https://apps.apple.com/app/id${APP_STORE_ID}`;
  const pt = input.providerToken === undefined ? APP_STORE_PROVIDER_TOKEN : input.providerToken;
  if (!pt || !/^\d+$/.test(pt)) return base;
  const ct = (input.campaign ?? '').replace(/[^A-Za-z0-9_.-]+/g, '-').slice(0, 30);
  return `${base}?pt=${pt}${ct ? `&ct=${encodeURIComponent(ct)}` : ''}&mt=8`;
}

export interface InstallReferral {
  clickId: string | null;
  code: string | null;
  utm: Utm;
}

/**
 * What Play's install referrer says about how this phone got DrBell — or
 * null when it says nothing of ours.
 *
 * Every Play install has a referrer; an organic one reads
 * `utm_source=google-play&utm_medium=organic`. Only a referrer carrying our
 * click id or a code is a referral; anything else is not claimed, so the
 * thousands of people who simply searched the store are never sent to the
 * server as if somebody had sent them.
 *
 * Play normally returns the string decoded once (exactly what
 * `playReferrer` built), but some paths return it still encoded
 * (`drbell_click%3D…`), so that is undone first.
 */
export function parseInstallReferrer(raw: string | null | undefined): InstallReferral | null {
  if (!raw) return null;
  let s = raw.trim();
  if (!s.includes('=') && /%3D/i.test(s)) {
    try {
      s = decodeURIComponent(s);
    } catch {
      return null;
    }
  }
  const params = parseQuery(s);
  const clickId = isClickId(params[REFERRER_CLICK]) ? params[REFERRER_CLICK]! : null;
  const code = normaliseReferralCode(params[REFERRER_CODE]);
  if (!clickId && !code) return null;
  return { clickId, code, utm: attributionFromQuery(params).utm };
}
